import { FC } from 'react'
import {
  Card,
  FormLabel,
  Modal,
  ModalProps,
  FormControl,
  Container,
  Button,
} from 'react-bootstrap'
import { FormProvider, useForm, Resolver } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { warehouseOptions, WareHouseSearchOptions } from 'shared/models/Enum'
import { IProductDimension } from 'shared/models/IProductDimension'
import { useServerValidation } from 'shared/hooks/useServerValidation'
import {
  type ProductDimensionFormData,
  productDimensionSchema,
} from './schemas/ProductDimensionSchema'
import DimensionFieldPair from './components/DimensionFieldPair'

interface IProps extends ModalProps {
  productDimension: IProductDimension | null
  isLoading: boolean
  errorMessage: string
  onSubmit: (productDimension: IProductDimension) => void
  onDelete: () => void
}

const DetailsModal: FC<IProps> = ({
  productDimension,
  isLoading,
  errorMessage,
  onSubmit,
  onDelete,
  ...modalProps
}) => {
  const formMethods = useForm<ProductDimensionFormData>({
    defaultValues: {
      ...productDimension,
      warehouseId: productDimension?.warehouseId ?? warehouseOptions[0],
    },
    mode: 'onSubmit',
    resolver: zodResolver(
      productDimensionSchema
    ) as Resolver<ProductDimensionFormData>,
  })

  const { errors } = formMethods.formState
  const { generalError } = useServerValidation<ProductDimensionFormData>(
    errorMessage,
    formMethods.setError
  )

  const handleSubmit = (data: IProductDimension) => {
    onSubmit(data)
  }

  return (
    <Modal {...modalProps}>
      <Modal.Header>
        <Modal.Title>
          {productDimension
            ? 'Productafmetingen – detail'
            : 'Nieuwe Productafmetingen'}
        </Modal.Title>
      </Modal.Header>
      <FormProvider {...formMethods}>
        <form onSubmit={formMethods.handleSubmit(handleSubmit)} noValidate>
          <fieldset disabled={isLoading}>
            <Modal.Body>
              {generalError && (
                <div className='alert alert-danger'>{generalError}</div>
              )}
              <Card className='mb-4'>
                <Card.Header className='font-weight-bold'>
                  Product eigenschappen
                </Card.Header>
                <Card.Body>
                  <div className='row'>
                    <div className='col-sm-4'>
                      <FormLabel htmlFor='productGroupCode'>
                        Productgroepcode
                      </FormLabel>
                      <FormControl
                        type='number'
                        id='productGroupCode'
                        className={errors.productGroupCode ? 'is-invalid' : ''}
                        disabled={!!productDimension?.productDimensionId}
                        {...formMethods.register('productGroupCode')}
                      />
                      {errors.productGroupCode && (
                        <div className='invalid-feedback'>
                          {errors.productGroupCode.message}
                        </div>
                      )}
                    </div>
                    <div className='col-sm-4'>
                      <FormLabel htmlFor='fustCode'>Fustcode</FormLabel>
                      <FormControl
                        type='number'
                        id='fustCode'
                        className={errors.fustCode ? 'is-invalid' : ''}
                        disabled={!!productDimension?.productDimensionId}
                        {...formMethods.register('fustCode')}
                      />
                      {errors.fustCode && (
                        <div className='invalid-feedback'>
                          {errors.fustCode.message}
                        </div>
                      )}
                    </div>
                    <div className='col-sm-4'>
                      <FormLabel htmlFor='warehouseId'>Vestiging</FormLabel>
                      <FormControl
                        as='select'
                        id='warehouseId'
                        className={errors.warehouseId ? 'is-invalid' : ''}
                        disabled={!!productDimension?.productDimensionId}
                        {...formMethods.register('warehouseId')}
                      >
                        {warehouseOptions.map(warehouse => (
                          <option key={warehouse} value={warehouse}>
                            {WareHouseSearchOptions[warehouse]}
                          </option>
                        ))}
                      </FormControl>
                      {errors.warehouseId && (
                        <div className='invalid-feedback'>
                          {errors.warehouseId.message}
                        </div>
                      )}
                    </div>
                  </div>
                  <p className='text-muted mt-2 mb-0'>
                    Bepaalt op welke combinaties de afmetingen gelden.
                  </p>
                </Card.Body>
              </Card>
              <Card>
                <Card.Header className='font-weight-bold'>
                  Afmetingen
                </Card.Header>
                <Card.Body>
                  <Container fluid>
                    <div className='row justify-content-around'>
                      <div className='row col-sm-4 mr-4'>
                        <DimensionFieldPair
                          mmField='depthInMM'
                          percentageField='depthInPercentage'
                          mmLabel='Diepte in mm'
                          percentageLabel='Diepte + %'
                        />
                      </div>
                      <div className='row col-sm-4 mr-4'>
                        <DimensionFieldPair
                          mmField='widthInMM'
                          percentageField='widthInPercentage'
                          mmLabel='Breedte in mm'
                          percentageLabel='Breedte + %'
                        />
                      </div>
                      <div className='row col-sm-4'>
                        <DimensionFieldPair
                          mmField='heightInMM'
                          percentageField='heightInPercentage'
                          mmLabel='Hoogte in mm'
                          percentageLabel='Hoogte + %'
                        />
                      </div>
                    </div>
                    {errors['no-dimensions'] && (
                      <p className='invalid-feedback d-block text-center mb-0'>
                        {errors['no-dimensions']?.message}
                      </p>
                    )}
                  </Container>
                </Card.Body>
              </Card>
            </Modal.Body>
            <Modal.Footer>
              {productDimension && (
                <Button variant='danger' type='button' onClick={onDelete}>
                  Verwijderen
                </Button>
              )}
              <Button variant='primary' type='submit'>
                Opslaan
              </Button>
              <Button
                variant='secondary'
                type='button'
                onClick={modalProps.onHide}
              >
                Sluiten
              </Button>
            </Modal.Footer>
          </fieldset>
        </form>
      </FormProvider>
    </Modal>
  )
}

export default DetailsModal
